import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, FileText, Link as LinkIcon, Calendar, Mail, Sparkles } from 'lucide-react';
import Header from '../components/layout/Header';
import api from '../services/api';

interface PrepData {
  client: {
    id: string;
    first_name: string;
    last_name?: string;
    email?: string;
    phone?: string;
    status?: string;
  };
  active_project?: {
    id: string;
    name: string;
    description?: string;
    status: string;
  } | null;
  notes: Array<{
    id: string;
    title: string;
    body?: string;
    category: string;
    updated_at?: string;
  }>;
  documents: Array<{
    id: string;
    title: string;
    content?: string;
    category: string;
  }>;
  links: Array<{
    id: string;
    title: string;
    url: string;
    category: string;
    notes?: string;
  }>;
  visits: {
    upcoming: Array<{
      id: string;
      visit_date: string;
      location?: string;
      notes?: string;
      status: string;
    }>;
    past: Array<{
      id: string;
      visit_date: string;
      location?: string;
      outcome?: string;
      billed_amount?: number;
      status: string;
    }>;
  };
  recent_emails: Array<{
    id: string;
    subject: string;
    from_email: string;
    snippet?: string;
    sent_at?: string;
  }>;
}

export default function ClientPrepPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [prep, setPrep] = useState<PrepData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<string>('prep');
  const [generating, setGenerating] = useState(false);
  const [generatedDoc, setGeneratedDoc] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    load();
  }, [id]);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get(`/clients/${id}/prep`);
      setPrep(data);
    } catch (err: any) {
      setError(err?.response?.data?.detail || err.message || 'Failed to load client prep');
    } finally {
      setLoading(false);
    }
  }

  async function generatePrepDoc() {
    setGenerating(true);
    try {
      const { data } = await api.post(`/clients/${id}/generate-prep-doc`);
      setGeneratedDoc(data.content);
    } catch (err: any) {
      setGeneratedDoc(`Error: ${err?.response?.data?.detail || err.message}`);
    } finally {
      setGenerating(false);
    }
  }

  function copyGenerated() {
    if (generatedDoc) navigator.clipboard.writeText(generatedDoc);
  }

  if (loading) {
    return (
      <div className="p-6">
        <Header title="Client Prep" />
        <div className="mt-6 text-gray-600">Loading client prep...</div>
      </div>
    );
  }

  if (error || !prep) {
    return (
      <div className="p-6">
        <Header title="Client Prep" />
        <div className="mt-6 text-red-600">{error || 'No data'}</div>
        <button onClick={() => navigate(-1)} className="mt-4 px-3 py-1 border rounded">
          Back
        </button>
      </div>
    );
  }

  const c = prep.client;
  const filteredNotes = activeTab === 'prep'
    ? prep.notes
    : prep.notes.filter(n => n.category === activeTab);

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto">
      <div className="flex items-center gap-2 mb-4">
        <button
          onClick={() => navigate(`/contacts/${id}`)}
          className="p-2 rounded hover:bg-gray-100"
          aria-label="Back to contact"
        >
          <ArrowLeft size={20} />
        </button>
        <h1 className="text-2xl font-bold flex-1">
          {c.first_name} {c.last_name || ''}
        </h1>
        <button
          onClick={generatePrepDoc}
          disabled={generating}
          className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-60 text-sm"
        >
          <Sparkles size={16} />
          {generating ? 'Generating...' : 'Generate Prep Doc'}
        </button>
      </div>

      {/* Quick Facts */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-4 bg-gray-50 rounded-lg mb-4 text-sm">
        <div>
          <div className="text-gray-600 text-xs uppercase">Email</div>
          <div className="text-gray-700">{c.email || '—'}</div>
        </div>
        <div>
          <div className="text-gray-600 text-xs uppercase">Phone</div>
          <div className="text-gray-700">{c.phone || '—'}</div>
        </div>
        <div>
          <div className="text-gray-600 text-xs uppercase">Status</div>
          <div className="text-gray-700">{c.status || '—'}</div>
        </div>
        <div>
          <div className="text-gray-600 text-xs uppercase">Project</div>
          <div className="text-gray-700">{prep.active_project?.name || '—'}</div>
        </div>
      </div>

      {prep.active_project?.description && (
        <div className="p-3 border-l-4 border-blue-500 bg-blue-50 mb-4 text-sm text-gray-700">
          {prep.active_project.description}
        </div>
      )}

      {/* Generated Doc Modal */}
      {generatedDoc && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-bold">AI-Generated Prep Document</h2>
              <button onClick={() => setGeneratedDoc(null)} className="text-gray-600">✕</button>
            </div>
            <pre className="p-4 overflow-auto text-sm whitespace-pre-wrap font-mono text-gray-800 flex-1">
              {generatedDoc}
            </pre>
            <div className="p-4 border-t flex gap-2 justify-end">
              <button onClick={copyGenerated} className="px-3 py-1 border rounded text-sm">
                Copy
              </button>
              <button onClick={() => setGeneratedDoc(null)} className="px-3 py-1 bg-gray-200 rounded text-sm">
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Notes */}
        <div className="border rounded-lg p-4 bg-white">
          <div className="flex items-center gap-2 mb-3">
            <FileText size={18} className="text-gray-700" />
            <h3 className="font-semibold text-gray-800">Notes ({prep.notes.length})</h3>
          </div>
          <div className="flex gap-1 mb-3 text-xs">
            {['prep', 'technical', 'billing', 'visit_history', 'general'].map(cat => (
              <button
                key={cat}
                onClick={() => setActiveTab(cat)}
                className={`px-2 py-1 rounded ${activeTab === cat ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'}`}
              >
                {cat}
              </button>
            ))}
          </div>
          <div className="space-y-2 max-h-80 overflow-auto">
            {filteredNotes.length === 0 && <div className="text-gray-600 text-sm">No notes in this category.</div>}
            {filteredNotes.map(n => (
              <details key={n.id} className="border rounded p-2 text-sm">
                <summary className="cursor-pointer font-medium text-gray-800">{n.title}</summary>
                {n.body && <div className="mt-2 text-gray-700 whitespace-pre-wrap">{n.body}</div>}
              </details>
            ))}
          </div>
        </div>

        {/* Recent Emails */}
        <div className="border rounded-lg p-4 bg-white">
          <div className="flex items-center gap-2 mb-3">
            <Mail size={18} className="text-gray-700" />
            <h3 className="font-semibold text-gray-800">Recent Emails ({prep.recent_emails.length})</h3>
          </div>
          <div className="space-y-2 max-h-80 overflow-auto">
            {prep.recent_emails.length === 0 && <div className="text-gray-600 text-sm">No linked emails.</div>}
            {prep.recent_emails.map(e => (
              <details key={e.id} className="border rounded p-2 text-sm">
                <summary className="cursor-pointer font-medium text-gray-800">{e.subject}</summary>
                <div className="mt-2 text-xs text-gray-600">From: {e.from_email}</div>
                {e.snippet && <div className="mt-1 text-gray-700">{e.snippet}</div>}
              </details>
            ))}
          </div>
        </div>

        {/* Documents */}
        <div className="border rounded-lg p-4 bg-white">
          <div className="flex items-center gap-2 mb-3">
            <FileText size={18} className="text-gray-700" />
            <h3 className="font-semibold text-gray-800">Documents ({prep.documents.length})</h3>
          </div>
          <div className="space-y-2 max-h-80 overflow-auto">
            {prep.documents.length === 0 && <div className="text-gray-600 text-sm">No documents.</div>}
            {prep.documents.map(d => (
              <details key={d.id} className="border rounded p-2 text-sm">
                <summary className="cursor-pointer font-medium text-gray-800">
                  <span className="text-xs text-gray-600 uppercase mr-2">[{d.category}]</span>
                  {d.title}
                </summary>
                {d.content && (
                  <pre className="mt-2 text-gray-700 whitespace-pre-wrap font-mono text-xs">
                    {d.content}
                  </pre>
                )}
              </details>
            ))}
          </div>
        </div>

        {/* Links */}
        <div className="border rounded-lg p-4 bg-white">
          <div className="flex items-center gap-2 mb-3">
            <LinkIcon size={18} className="text-gray-700" />
            <h3 className="font-semibold text-gray-800">Links ({prep.links.length})</h3>
          </div>
          <div className="space-y-2 max-h-80 overflow-auto">
            {prep.links.length === 0 && <div className="text-gray-600 text-sm">No links.</div>}
            {prep.links.map(l => (
              <a
                key={l.id}
                href={l.url}
                target="_blank"
                rel="noopener noreferrer"
                className="block border rounded p-2 text-sm hover:bg-gray-50"
              >
                <div className="text-xs text-gray-600 uppercase">[{l.category}]</div>
                <div className="font-medium text-blue-700">{l.title}</div>
                <div className="text-xs text-gray-600 truncate">{l.url}</div>
              </a>
            ))}
          </div>
        </div>

        {/* Upcoming Visits */}
        <div className="border rounded-lg p-4 bg-white">
          <div className="flex items-center gap-2 mb-3">
            <Calendar size={18} className="text-gray-700" />
            <h3 className="font-semibold text-gray-800">Upcoming Visits ({prep.visits.upcoming.length})</h3>
          </div>
          <div className="space-y-2 max-h-80 overflow-auto">
            {prep.visits.upcoming.length === 0 && <div className="text-gray-600 text-sm">No upcoming visits.</div>}
            {prep.visits.upcoming.map(v => (
              <div key={v.id} className="border rounded p-2 text-sm">
                <div className="font-medium text-gray-800">{new Date(v.visit_date).toLocaleString()}</div>
                {v.location && <div className="text-gray-700">{v.location}</div>}
                {v.notes && <div className="text-gray-700 mt-1 text-xs">{v.notes}</div>}
                <div className="text-xs text-gray-600 uppercase mt-1">{v.status}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Past Visits */}
        <div className="border rounded-lg p-4 bg-white">
          <div className="flex items-center gap-2 mb-3">
            <Calendar size={18} className="text-gray-700" />
            <h3 className="font-semibold text-gray-800">Past Visits ({prep.visits.past.length})</h3>
          </div>
          <div className="space-y-2 max-h-80 overflow-auto">
            {prep.visits.past.length === 0 && <div className="text-gray-600 text-sm">No past visits.</div>}
            {prep.visits.past.map(v => (
              <div key={v.id} className="border rounded p-2 text-sm">
                <div className="font-medium text-gray-800">{new Date(v.visit_date).toLocaleDateString()}</div>
                {v.outcome && <div className="text-gray-700 text-xs mt-1">{v.outcome}</div>}
                {v.billed_amount != null && (
                  <div className="text-green-700 text-xs mt-1">Billed: ${v.billed_amount}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
